import * as RadixAvatar from "@radix-ui/react-avatar";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Avatar — Radix wrapper.
 *
 * Why SVG, not flex+nudge?  Pretendard Variable's cap-box sits slightly
 * above the glyph box centre. Flex centring + an `em` translate gets us
 * close but never perfect across sizes / scripts. Rendering the initials
 * inside an inline SVG with `dominant-baseline: central` puts the cap-box
 * centre *exactly* on the geometric centre of the circle — invariant to
 * font metrics, font size, and `zoom`.
 */

export const Avatar = forwardRef<
  HTMLSpanElement,
  ComponentPropsWithoutRef<typeof RadixAvatar.Root>
>(({ className, ...p }, ref) => (
  <RadixAvatar.Root
    ref={ref}
    className={cn(
      "inline-flex items-center justify-center overflow-hidden rounded-full",
      "h-8 w-8 bg-[var(--bg-subtle)] text-[12px] font-semibold text-[var(--fg-muted)]",
      "uppercase tracking-tight tabular-nums select-none",
      className,
    )}
    {...p}
  />
));
Avatar.displayName = "Avatar";

export const AvatarImage = forwardRef<
  HTMLImageElement,
  ComponentPropsWithoutRef<typeof RadixAvatar.Image>
>(({ className, ...p }, ref) => (
  <RadixAvatar.Image
    ref={ref}
    className={cn("h-full w-full object-cover", className)}
    {...p}
  />
));
AvatarImage.displayName = "AvatarImage";

export const AvatarFallback = forwardRef<
  HTMLSpanElement,
  ComponentPropsWithoutRef<typeof RadixAvatar.Fallback>
>(({ className, children, ...p }, ref) => (
  <RadixAvatar.Fallback
    ref={ref}
    className={cn("block h-full w-full", className)}
    {...p}
  >
    <CenteredInitials>{children}</CenteredInitials>
  </RadixAvatar.Fallback>
));
AvatarFallback.displayName = "AvatarFallback";

function CenteredInitials({ children }: { children: ReactNode }) {
  const text = flatten(children);
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="xMidYMid meet"
      className="block h-full w-full"
      role="presentation"
    >
      <text
        x="50"
        y="50"
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily="inherit"
        fontSize="48"
        fontWeight="600"
        letterSpacing="-1"
        fill="currentColor"
      >
        {text}
      </text>
    </svg>
  );
}

/** Flatten a tiny React tree of strings / numbers / Fragments to a plain string. */
function flatten(node: ReactNode): string {
  if (node == null || node === false) return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(flatten).join("");
  if (typeof node === "object" && "props" in node) {
    return flatten((node as { props?: { children?: ReactNode } }).props?.children);
  }
  return "";
}
